import { useEffect, useRef, useState, forwardRef, useImperativeHandle, Fragment } from 'react';
import './precision-bar.css';

interface PrecisionBarProps {
  onStop: (position: number) => void;
  direction: 'horizontal' | 'vertical';
  focus?: boolean;
  className?: string;
}

export interface PrecisionBarHandle {
  focus: () => void;
}

export const PrecisionBar = forwardRef<PrecisionBarHandle, PrecisionBarProps>(
  ({ onStop, direction, focus, className }, ref) => {
    const [position, setPosition] = useState(0);
    const animationRef = useRef<number>(0);
    const startTimeRef = useRef<number>(Date.now());
    const lastTimeRef = useRef<number>(Date.now());
    const percentageRef = useRef<number>(1);
    const overlayRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        overlayRef.current?.focus();
      }
    }));

  useEffect(() => {
    const period = 1500; // Controls the speed of oscillation
    const basePower = 2.6;
    const power = Math.pow(basePower, 1000/period);

    const animate = () => {
      const now = Date.now();
      const deltaTime = now - lastTimeRef.current;
      lastTimeRef.current = now;
      
      const elapsed = now - startTimeRef.current;

      // Use sine wave for oscillation, with easing to make it slow at 0% and fast at 100%
      // The sine wave naturally creates the oscillation
      // We use a custom easing function to make it slower at the bottom

      const elapsedInPeriod = (elapsed % period) / period;
      // Convert to percentage (0-100)
      const current = Math.max(percentageRef.current, 1);
      percentageRef.current = elapsedInPeriod < 0.5 ? 
        current * Math.pow(power, deltaTime/100)
        : current / Math.pow(power, deltaTime/100);

      setPosition(percentageRef.current);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    onStop(position);
  };

  const classes = ['precision-bar-container'];
  if (className) classes.push(className);
  if (direction === "vertical") classes.push('vertical');

  const barSize = Math.max(0, 101-percentageRef.current);
  const dynamicStyle = direction === "vertical" ? {height: `${barSize}%`} : {width: `${barSize}%`};

  const overlay = focus === false ? "" : <div ref={overlayRef} className="precision-bar-overlay" onKeyDown={handleClick} onClick={handleClick} tabIndex={0}/>;

  return (
    <Fragment>
      {overlay}
      <div className={classes.join(' ')}>
        <div className="precision-bar-track">
          <div
            className="precision-bar-fill"
            style={dynamicStyle}
          />
        </div>
      </div>
    </Fragment>
  );
});
